import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const LANGUAGE_STORAGE_KEY = 'mentory.systemLanguage.v1';

export const LANGUAGE_OPTIONS = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'en', label: 'English' },
];

const SUPPORTED_LANGUAGE_VALUES = new Set(LANGUAGE_OPTIONS.map((item) => item.value));

const en = {
  'app.route.mentorProfile': 'Mentor Profile',
  'app.route.courseRequest': 'Post Request',
  'app.route.favorites': 'Favorites',
  'app.route.favoriteCollection': 'Favorite Collection',
  'app.route.recentVisits': 'Recent Visits',
  'app.route.courses': 'Courses',
  'app.route.messages': 'Messages',
  'app.route.wallet': 'Wallet',
  'app.route.settings': 'Settings',
  'app.route.help': 'Help Center',
  'app.route.profileEditor': 'Edit Profile Card',
  'app.route.mentorCourses': 'Mentor Courses',
  'app.route.requestDetail': 'Course Request Detail',
  'app.route.classroom': 'Classroom',
  'app.route.mentorContract': 'Mentor Agreement',

  'common.loading': 'Loading...',
  'common.loadingEllipsis': 'Loading...',
  'common.notProvided': 'Not provided',
  'common.notActivated': 'Not activated',
  'common.loginFirst': 'Please log in first',
  'common.loadFailed': 'Failed to load',
  'common.unset': 'Not set',
  'common.save': 'Save',
  'common.saving': 'Saving...',
  'common.saved': 'Saved',
  'common.edit': 'Edit',
  'common.login': 'Log In',
  'common.register': 'Register',
  'common.logout': 'Log Out',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.deleting': 'Deleting...',
  'common.create': 'Create',
  'common.creating': 'Creating...',
  'common.confirm': 'Confirm',
  'common.done': 'Done',
  'common.multiSelect': 'Select',
  'common.processing': 'Processing...',
  'common.select': 'Select',
  'common.deselect': 'Deselect',
  'common.selected': 'Selected',
  'common.change': 'Change',
  'common.cancel': 'Cancel',
  'common.modify': 'Change',
  'common.reset': 'Reset',
  'common.optional': 'Optional',
  'common.search': 'Search',
  'common.uploading': 'Uploading...',
  'common.uploadFailed': 'Upload failed. Please try again later.',
  'common.avatarTooLarge': 'Avatar image must be 5 MB or smaller',
  'common.chooseImageFile': 'Please choose an image file',
  'common.incompleteSignature': 'Upload signature response is incomplete',
  'common.ossCorsUploadFailed': 'Upload failed. Please check the OSS CORS configuration.',
  'common.avatarUpdated': 'Avatar updated',
  'common.saveFailed': 'Save failed. Please try again later.',
  'common.pendingReminders': 'Pending reminders',
  'common.menuMore': 'More menu',
  'common.openMentor': 'Activate',
  'common.success': 'Success',
  'common.autoClose': 'Will close automatically',
  'common.newCourseAlert': 'New course alert',
  'common.unreadMessages': 'Unread messages',
  'common.badgeCount': '{label}: {count}',
  'common.clearFilter': 'Clear {label}',
  'common.confirmAction': 'Confirm Action',
  'auth.sessionExpired': 'Session expired. Please log in again.',
  'auth.unavailableDuringMentorReview': 'Unavailable while mentor account is under review',
  'auth.profileEditUnavailable': 'Profile card cannot be edited now',
  'auth.mentorPendingCannotEdit': 'Mentor account is under review. Profile editing is unavailable.',
  'auth.actionFailed': 'Action failed. Please try again later.',
  'auth.emailRequired': 'Enter your email',
  'auth.emailOrIdRequired': 'Enter email, StudentID, or MentorID',
  'auth.registerEmailRequired': 'Enter your registered email',
  'auth.emailInvalid': 'Enter a valid email address',
  'auth.passwordRequired': 'Enter your password',
  'auth.passwordMin': 'Password must be at least 6 characters',
  'auth.passwordMismatch': 'The two passwords do not match',
  'auth.roleRequired': 'Choose a role',
  'auth.submitInvalid': 'Submitted information is invalid',
  'auth.networkError': 'Network error. Check your connection and try again.',
  'auth.loginFailed': 'Login failed. Please try again later.',
  'auth.loginInvalid': 'Email or password is incorrect',
  'auth.loggingIn': 'Logging in...',
  'auth.resetFailed': 'Password reset failed. Please try again later.',
  'auth.resetDone': 'Password reset. Please log in with your new password.',
  'auth.registerFailed': 'Registration failed. Please try again later.',
  'auth.registerDone': 'Registration successful. You are logged in.',
  'auth.mentorApplicationSubmitted': 'Account created. Your mentor application has been submitted.',
  'auth.registerClosing': 'Registration successful. Closing...',
  'auth.autoLoginFailed': 'Registration successful, but auto-login failed. Please log in manually.',
  'auth.emailRegistered': 'This email is already registered',
  'auth.loginTitle': 'Log In',
  'auth.resetTitle': 'Reset Password',
  'auth.loginWelcome': 'Welcome back to Mentory',
  'auth.resetEmailTitle': 'Verify with your email',
  'auth.resetPasswordTitle': 'Set a new password',
  'auth.emailOrIdPlaceholder': 'Enter email, StudentID, or MentorID',
  'auth.passwordPlaceholder': 'Enter password',
  'auth.registerEmailPlaceholder': 'Enter registered email',
  'auth.newPasswordPlaceholder': 'Enter new password',
  'auth.confirmNewPasswordPlaceholder': 'Enter new password again',
  'auth.hidePassword': 'Hide password',
  'auth.showPassword': 'Show password',
  'auth.forgotPassword': 'Forgot password?',
  'auth.backToLogin': 'Back to login',
  'auth.goRegister': 'Register',
  'auth.continue': 'Continue',
  'auth.sendCode': 'Send Code',
  'auth.resetPassword': 'Reset Password',
  'auth.socialUnavailable': 'Or (coming soon)',
  'auth.wechatLogin': 'Continue with WeChat',
  'auth.googleLogin': 'Continue with Google',
  'auth.resetCodeTitle': 'Reset Password Code',
  'auth.registerTitle': 'Register',
  'auth.registerWelcome': 'Welcome to Mentory',
  'auth.termsRequired': 'Please read and agree to the Terms of Service',
  'auth.privacyRequired': 'Please read the Privacy Policy',
  'auth.termsUpdated': 'The Terms of Service have been updated. Please review and agree again.',
  'auth.privacyUpdated': 'The Privacy Policy has been updated. Please review it again.',
  'auth.termsPrefix': 'I have read and agree to the',
  'auth.privacyPrefix': 'I have read and understand the',
  'auth.legalPolicyJoin': 'and the',
  'auth.emailPlaceholder': 'Enter email',
  'auth.confirmPasswordPlaceholder': 'Confirm password',
  'auth.sameEmailTip': 'The same email can register both roles. Complete student and mentor registration separately.',
  'auth.studentRole': 'I am a student',
  'auth.mentorRole': 'I am a mentor (review required)',
  'auth.studentWelcomeTitle': 'Welcome to Mentory!',
  'auth.studentIdLine': 'This is your StudentID:',
  'auth.mentorWelcomeTitle': 'Welcome to Mentory as a mentor!',
  'auth.mentorIdLine': 'This is your MentorID:',
  'auth.gotIt': 'Got it',
  'auth.needEmailCodeFirst': 'Complete email verification first',

  'welcome.studentIdHelp': 'Your StudentID is your unique Mentory identifier and is available in Personal Information.',
  'welcome.mentorIdHelp': 'Your MentorID is your unique Mentory identifier and can be used for display and search.',
  'welcome.studentNoticeTitle': 'Before you get started',
  'welcome.mentorNoticeTitle': 'Before you start teaching',
  'welcome.studentProfileTitle': 'Complete your personal information',
  'welcome.profileLink': 'Personal Information',
  'welcome.studentProfileBody': ' helps mentors understand your learning background and needs.',
  'welcome.studentReviewRewardTitle': 'Review a mentor and earn lesson hours',
  'welcome.studentHoursConfirmationTitle': 'Confirm your lesson hours promptly',
  'welcome.studentHoursConfirmationBody': 'Within one week after each lesson, please log back in to Mentory to review and confirm the lesson hours used.',
  'welcome.studentReviewRewardPrefix': 'Limited-time offer: after completing a lesson, review your mentor on the ',
  'welcome.coursesLink': 'Courses',
  'welcome.studentReviewRewardBody': ' page to receive free lesson hours.',
  'welcome.studentSafetyTitle': 'Protect your learning and transactions',
  'welcome.studentSafetyPrefix': 'Mentory has zero tolerance for ghostwriting or mentors seeking private contact. If you encounter this behavior or are dissatisfied with teaching quality, submit a complaint on the ',
  'welcome.studentSafetyBody': ' page. The platform will provide generous rewards.',
  'welcome.studentEmailTitle': 'Receive timely email reminders',
  'welcome.studentEmailBody': 'Mentory sends timely email reminders throughout booking. If an email is missing, check your spam folder.',
  'welcome.mentorProfileTitle': 'Complete your mentor profile',
  'welcome.mentorProfileBody': 'Add your education, subject expertise, and introduction so students can understand you more quickly.',
  'welcome.mentorEfficiencyTitle': 'Improve booking efficiency',
  'welcome.mentorEfficiencyPrefix': 'In ',
  'welcome.settingsLink': 'Settings',
  'welcome.mentorEfficiencyBody': ', set your available teaching times under Personal Information to make booking more efficient. In ',
  'welcome.notificationsLink': 'Notification settings',
  'welcome.mentorNotificationsBody': ', choose your preferred course directions and types, and enable new course request alerts to receive matching requests by email.',
  'welcome.mentorRulesTitle': 'Mentor conduct',
  'welcome.mentorRulesBody': 'Mentory has zero tolerance for ghostwriting or seeking private contact with students. Keep communication, booking, and teaching on the platform. Violations will be handled under platform rules.',
  'welcome.editProfileCard': 'Edit personal card',
  'welcome.exploreMentors': 'Explore mentors',

  'emailCode.title': 'Email Verification Code',
  'emailCode.sentToPrefix': 'Code sent to',
  'emailCode.sentTo': 'Code sent to {email}',
  'emailCode.checkSpamTip': "If you don't receive the email, please check your spam folder.",
  'emailCode.resending': 'Sending...',
  'emailCode.resend': 'Resend',
  'emailCode.invalidCode': 'Enter the 6-digit code',
  'emailCode.verifyFailed': 'Code verification failed. Please try again later.',
  'emailCode.sendFailed': 'Failed to send code. Please try again later.',
  'emailCode.verifying': 'Verifying...',
  'emailCode.resendAfter': 'Resend in {seconds}s',

  'mentorActivation.title': 'Welcome to Mentory Mentor',
  'mentorActivation.uploadSignatureFailed': 'Failed to get upload signature',
  'mentorActivation.uploadFailed': 'Resume upload failed',
  'mentorActivation.uploadFailedRetry': 'Resume upload failed. Please try again later.',
  'mentorActivation.failedFiles': 'These files failed to upload: {files}',
  'mentorActivation.deleteFailed': 'Failed to delete resume. Please try again later.',
  'mentorActivation.required': 'Upload your resume first',
  'mentorActivation.submitFailed': 'Submit failed. Please try again later.',
  'mentorActivation.uploading': 'Uploading...',
  'mentorActivation.uploadResume': 'Upload Resume',
  'mentorActivation.uploaded': 'Uploaded:',
  'mentorActivation.deleteFile': 'Delete file {name}',
  'mentorActivation.submitting': 'Submitting...',
  'mentorContract.kicker': 'Mentor Agreement',
  'mentorContract.title': 'Review and sign the mentor agreement',
  'mentorContract.modalTitle': 'Review and sign the mentor agreement',
  'mentorContract.modalSubtitle': 'Enter your legal name, review the complete agreement, and sign with the code sent to your registered email.',
  'mentorContract.closeModal': 'Close mentor agreement',
  'mentorContract.subtitle': 'Review the complete agreement, confirm your name and registered email, then sign with an email verification code.',
  'mentorContract.signedTitle': 'My mentor agreement',
  'mentorContract.pendingApprovalTitle': 'Mentor review is not complete',
  'mentorContract.pendingApprovalText': 'After approval, Mentory will guide you here to review and sign the mentor agreement.',
  'mentorContract.contractInfo': 'Agreement information',
  'mentorContract.name': 'Name',
  'mentorContract.legalName': 'Legal name on agreement',
  'mentorContract.legalNameLabel': 'Party B legal name',
  'mentorContract.legalNamePlaceholder': 'Enter your legal name',
  'mentorContract.legalNameHelp': 'This name will be written into the final agreement. Your Mentory nickname or profile display name will not be used automatically',
  'mentorContract.legalNameInvalid': 'Enter a legal name between 2 and 120 characters.',
  'mentorContract.refreshPreview': 'Update preview with this name',
  'mentorContract.refreshingPreview': 'Updating preview...',
  'mentorContract.retryPreview': 'Reload preview',
  'mentorContract.previewNameFirst': 'Enter your legal name and update the agreement preview first',
  'mentorContract.registeredEmail': 'Registered email',
  'mentorContract.number': 'Agreement number',
  'mentorContract.generatedOnSign': 'Generated when signing',
  'mentorContract.version': 'Agreement version',
  'mentorContract.signed': 'Signed',
  'mentorContract.signedAt': 'Signed at',
  'mentorContract.downloadPdf': 'Download final PDF',
  'mentorContract.documentPreview': 'Complete agreement',
  'mentorContract.fullAgreement': 'Complete official agreement',
  'mentorContract.openSeparate': 'Open separately',
  'mentorContract.loadingPdf': 'Generating agreement preview...',
  'mentorContract.openPdfFallback': 'Open agreement PDF',
  'mentorContract.previewUnavailable': 'Agreement preview is temporarily unavailable. Please try again later.',
  'mentorContract.previewGenerationFailed': 'The personalised agreement preview could not be generated. Please try again later.',
  'mentorContract.signingArea': 'Signing confirmations',
  'mentorContract.taxResidencyQuestion': 'Are you a Chinese tax resident?',
  'mentorContract.taxResidencyHelpLabel': 'View the Chinese tax resident explanation',
  'mentorContract.taxResidencyHelp': 'This generally means an individual who is domiciled in China, or who is not domiciled in China but stays in China for 183 days or more in a tax year. Mentory uses this selection to determine tax withholding. If you select Yes, Mentory will pay the applicable taxes for you in mainland China; if you select No, the tax-paying entity will be overseas. If you are unsure, consult a qualified tax professional first.',
  'mentorContract.taxResidencyYes': 'Yes',
  'mentorContract.taxResidencyNo': 'No',
  'mentorContract.taxResidencyRequired': 'Select whether you are a Chinese tax resident.',
  'mentorContract.taxResidencyChanged': 'Your tax residency selection changed. Send a new verification code.',
  'mentorContract.agreeCheckbox': 'I have read and agree to the Mentory Mentor Agreement',
  'mentorContract.infoCheckbox': 'I confirm that my name and related information in the agreement are true and accurate',
  'mentorContract.infoCheckboxWithName': 'I confirm that the Party B name “{name}” and related information are true and accurate',
  'mentorContract.nextStep': 'Next',
  'mentorContract.confirmAndSign': 'Confirm and Sign',
  'mentorContract.sendingCode': 'Sending verification code...',
  'mentorContract.enterCode': 'Enter the 6-digit code sent to your registered email',
  'mentorContract.codeDigit': 'Verification code digit {position}',
  'mentorContract.generating': 'Generating and archiving...',
  'mentorContract.completeSigning': 'Complete Signing',
  'mentorContract.sendingCodeShort': 'Sending...',
  'mentorContract.resendCode': 'Resend code',
  'mentorContract.codeSent': 'A 6-digit code was sent to your Mentory registered email',
  'mentorContract.generatingNotice': 'Generating the DOCX, converting the PDF, and securely archiving it. Do not submit again...',
  'mentorContract.signedSuccess': 'Agreement signed successfully. The final document has been frozen and archived.',
  'mentorContract.alreadySigned': 'This agreement is already signed. No duplicate submission is needed.',
  'mentorContract.codeInvalid': 'The code is incorrect. Try again.',
  'mentorContract.codeExpired': 'The code has expired. Send a new code.',
  'mentorContract.codeTooMany': 'Too many incorrect attempts. Send a new code.',
  'mentorContract.codeRateLimit': 'A code was sent recently. Please wait before trying again.',
  'mentorContract.codeSendFailed': 'Failed to send the code. Please try again later.',
  'mentorContract.codeNotFound': 'The code is missing or no longer valid. Send a new code.',
  'mentorContract.codeFormat': 'Enter the 6-digit code.',
  'mentorContract.confirmationsRequired': 'Select both confirmations before signing.',
  'mentorContract.processing': 'The agreement is being generated. Do not submit again.',
  'mentorContract.generationFailed': 'The agreement could not be generated or archived. Please try again later.',
  'mentorContract.notSigned': 'No signed agreement was found.',
  'mentorContract.storageUnavailable': 'Agreement storage is temporarily unavailable. Please try again later.',
  'mentorContract.mentorRequired': 'Only mentors can access this page.',
  'mentorContract.notApproved': 'You can sign the agreement after your mentor application is approved.',
  'mentorContract.nameRequired': 'Complete your legal name before signing the agreement.',
  'mentorContract.emailRequired': 'No registered mentor email was found. Contact Mentory support.',
  'mentorContract.actionFailed': 'Action failed. Please try again later.',
  'lessonHours.respondFailed': 'Failed to process lesson-hour confirmation. Please try again later.',
  'lessonHours.disputeInvalid': 'Enter the correct hours in 0.25-hour increments.',
  'lessonHours.disputeTitle': 'Enter the correct lesson hours',

  'courseReminder.courseFallback': 'Course',
  'courseReminder.student': 'Student',
  'courseReminder.mentor': 'Mentor',
  'courseReminder.startedTitle': 'Class has started',
  'courseReminder.upcomingTitle': 'Class starts soon',
  'courseReminder.elapsedLabel': 'Started',
  'courseReminder.countdownLabel': 'Starts in',
  'courseReminder.close': 'Close class reminder',
  'courseReminder.kicker': 'Class reminder',
  'courseReminder.remaining': 'About {time} left',
  'courseReminder.dismiss': 'Got it',
  'courseReminder.enter': 'Enter Classroom',

  'nav.students': 'Students',
  'nav.mentors': 'Mentors',
  'nav.publishCourseRequest': 'Post Request',
  'nav.editProfileCard': 'Edit Profile Card',
  'nav.timeZone': 'Time zone',
  'nav.chooseMentorTimeZone': 'Choose mentor time zone',
  'nav.chooseStudentTimeZone': 'Choose student time zone',
  'nav.mentorSpecialty': 'Mentor specialty',
  'nav.chooseMentorSpecialty': 'Choose mentor specialty',
  'nav.courseType': 'Course type',
  'nav.chooseCourseType': 'Choose course type',
  'nav.firstLessonDate': 'First lesson date',
  'nav.chooseFirstLessonDate': 'Choose first lesson date',
  'nav.exactSearch': 'Exact search',
  'nav.exactSearchPlaceholder': 'Enter MentorID or mentor name',
  'nav.loadingTimeZoneFilter': 'Loading time zone filter...',
  'nav.loadingMentorSpecialty': 'Loading mentor specialty...',
  'nav.loadingMenu': 'Loading menu...',
  'nav.loadingCourseDrafts': 'Loading course drafts...',
  'nav.homeFilters': 'Home filters',
  'nav.reviewingCannotEdit': 'Under review. Editing is unavailable.',
  'nav.editUnavailable': 'Profile card cannot be edited now',
  'nav.mentorReviewing': 'Mentor account is under review. Editing is unavailable.',
  'nav.actionFailed': 'Action failed. Please try again later.',

  'mentorFilter.highRating': 'High rating',
  'mentorFilter.experienced': 'Experienced',
  'mentorFilter.fastResponse': 'Fast response',
  'mentorFilter.bilingualTeaching': 'Bilingual teaching',
  'mentorFilter.qsTop100': 'QS Top 100',
  'mentorFilter.clearSelection': 'Clear selection',
  'courseType.clearSelection': 'Clear course type selection',
  'startDate.urgent': 'Urgent <1d',
  'startDate.soon': 'Soon <3d',
  'startDate.withinWeek': 'Within a week',
  'startDate.longTerm': 'Long term',
  'startDate.resetAria': 'Reset first lesson date',

  'courses.title': 'Courses',
  'courses.loginRequired': 'Please log in to view courses',
  'courses.studentLoadFailed': 'Failed to load student courses',
  'courses.mentorLoadFailed': 'Failed to load mentor courses',
  'courses.loadFailed': 'Failed to load courses. Please try again later.',
  'courses.partialLoadFailed': 'Some courses failed to load. Showing successfully loaded courses only.',
  'courses.loading': 'Loading...',
  'courses.loadFailedTitle': 'Failed to load',
  'courses.tryAgain': 'Please try again later',
  'courses.emptyTitle': 'No courses yet',
  'courses.emptySubtitle': 'Courses will appear here after you create or accept them',
  'courses.month': '{month}',
  'courses.replaySoon': 'Replay will be available soon',
  'courses.replayDialogAria': 'Course replay',
  'courses.replayDialogTitle': 'View Replay',
  'courses.replayLoading': 'Loading replay...',
  'courses.replayLoadFailed': 'Failed to load replay',
  'courses.replayStorageUnconfigured': 'Replay storage is not configured. Please try again later.',
  'courses.replayEmptyTitle': 'No MP4 replay yet',
  'courses.replayEmptyDesc': 'This lesson does not have a playable MP4 recording file yet.',
  'courses.replayFileFallback': 'Lesson replay {index}',
  'courses.replayPlay': 'Play',
  'courses.invalidCourseId': 'Course information is invalid. Please refresh and try again.',
  'courses.invalidReviewScores': 'Please choose a score from 1 to 5 for each review dimension.',
  'courses.invalidReviewComment': 'Written review can be up to 1000 characters.',
  'courses.courseNotFound': 'This course was not found and cannot be reviewed.',
  'courses.courseNotCompleted': 'This course has not ended yet and cannot be reviewed.',
  'courses.submitReviewFailed': 'Failed to submit review. Please try again later.',
  'courses.lessonHoursInvalid': 'Enter valid lesson hours in 0.25-hour increments.',
  'courses.submitLessonHoursFailed': 'Failed to submit lesson hours. Please try again later.',
  'courses.enterClassroom': 'Enter Classroom',
  'courses.checkingHours': 'Checking hours...',
  'courses.hoursLoadFailed': 'Failed to load lesson-hour info',
  'courses.goTopUp': 'Top Up',
  'courses.viewReplay': 'View Replay',
  'courses.myReview': 'My Review',
  'courses.reviewMentor': 'Review Mentor',
  'courses.courseDispute': 'Have a concern about this lesson?',
  'courseDispute.title': 'Lesson Concern',
  'courseDispute.close': 'Close lesson concern dialog',
  'courseDispute.courseInfo': 'Related lesson',
  'courseDispute.courseFallback': 'Lesson',
  'courseDispute.typeFallback': 'Other lesson type',
  'courseDispute.date': 'Date',
  'courseDispute.time': 'Teaching time',
  'courseDispute.duration': 'Duration',
  'courseDispute.mentor': 'Mentor',
  'courseDispute.reasonLabel': 'Issue type',
  'courseDispute.descriptionLabel': 'What happened',
  'courseDispute.descriptionPlaceholder': 'Describe what happened and how it differed from what was agreed',
  'courseDispute.resolutionLabel': 'Preferred resolution',
  'courseDispute.submitting': 'Submitting...',
  'courseDispute.submit': 'Submit Concern',
  'courseDispute.submittedStatus': 'Concern submitted',
  'courseDispute.submittedHint': 'The platform is reviewing it. You do not need to submit it again.',
  'courseDispute.resolvedStatus': 'Concern resolved',
  'courseDispute.resolvedHint': 'The final platform decision is shown below.',
  'courseDispute.actionPendingStatus': 'Resolution in progress',
  'courseDispute.actionPendingHint': 'The refund or lesson credit is being processed. Check again later.',
  'courseDispute.outcomeLabel': 'Outcome',
  'courseDispute.resolvedHours': 'Hours handled',
  'courseDispute.resultMessage': 'Platform note',
  'courseDispute.referenceId': 'Reference',
  'courseDispute.submittedAt': 'Submitted',
  'courseDispute.invalidReason': 'Choose an issue type.',
  'courseDispute.invalidResolution': 'Choose a preferred resolution.',
  'courseDispute.invalidDescription': 'Describe what happened using no more than 2000 characters.',
  'courseDispute.courseNotFound': 'This lesson was not found and a concern cannot be submitted.',
  'courseDispute.courseNotCompleted': 'This lesson has not ended yet and a concern cannot be submitted.',
  'courseDispute.submitFailed': 'Submission failed. Please try again later.',
  'courseDispute.successTitle': 'Concern Submitted',
  'courseDispute.successDescription': 'Mentory will review the lesson record and contact you through in-app messages or your registered email.',
  'courses.fillLessonHours': 'Fill Hours',
  'courses.lessonHoursLocked': 'The student has confirmed the hours. Editing is unavailable.',
  'courses.submitLessonHoursTitle': 'Submit actual hours for this lesson',
  'courses.reviewUpdatedTitle': 'Review Updated',
  'courses.reviewUpdatedDesc': 'Your review has been updated and the new scores replaced the previous record.',
  'courses.reviewSubmittedTitle': 'Thanks for your feedback',
  'courses.reviewSubmittedDesc': 'Your review has been submitted. We will use your feedback to improve mentor service.',
  'courses.reviewRewardBadge': 'Earn hours',
  'courses.reviewRewardGrantedTitle': 'Review Submitted',
  'courses.reviewRewardGrantedDesc': '0.25 lesson hours have been added to your balance.',
  'courses.mentorLoginRequired': 'Please log in with a mentor account',
  'courses.accessDenied': 'This account does not have access',
  'courses.studentCoursesLoadFailed': 'Failed to load student courses',
  'courses.mentorCoursesLoadFailed': 'Failed to load mentor courses',
  'courses.retry': 'Retry',
  'courses.loginOrRegister': 'Log in / Register',
  'courses.switchAccount': 'Switch Account',
  'courses.mentorCalendarHint': 'Log in to access the mentor course calendar',
  'courses.mentorReadyTitle': 'You are ready',
  'courses.mentorReadyDesc': 'We will finish the mentor review soon. After approval, you can access mentor courses.',
  'courses.mentorOnlyTitle': 'Mentors Only',
  'courses.mentorOnlyHint': 'Please log in with a mentor account to view this page',
  'courses.reviewComingSoon': 'Follow-up will be available soon',
  'courseDetail.aria': 'Course details',
  'courseDetail.close': 'Close course details',
  'courseDetail.courseType': 'Course Type',
  'courseDetail.date': 'Date',
  'courseDetail.time': 'Time',
  'courseDetail.duration': 'Duration',
  'courseReview.aria': 'Review mentor',
  'courseReview.close': 'Close review dialog',
  'courseReview.category.clarity': 'Clear explanation',
  'courseReview.category.communication': 'Smooth communication',
  'courseReview.category.preparation': 'Well prepared',
  'courseReview.category.expertise': 'Professional knowledge',
  'courseReview.category.punctuality': 'Punctual',
  'courseReview.scoreGroupAria': '{label} rating',
  'courseReview.scoreAria': '{label} {score} points',
  'courseReview.unrated': 'Not rated',
  'courseReview.commentLabel': 'Written review (optional)',
  'courseReview.commentPlaceholder': 'Share your experience, takeaways, or suggestions from this lesson as a reference for other students',
  'courseReview.update': 'Update Review',
  'courseReview.submit': 'Submit Review',
  'courseReview.submitting': 'Submitting...',
  'courseReview.rewardDescription': 'Review this mentor for the first time and receive 0.25 lesson hours after submitting.',

  'courseRequest.step.direction.label': 'Step 1',
  'courseRequest.step.direction.title': 'Define Your Learning Direction',
  'courseRequest.step.direction.description': 'We will help align your goals and expectations and identify the subject area you want to study.',
  'courseRequest.step.details.label': 'Step 2',
  'courseRequest.step.details.title': 'Add Course Details',
  'courseRequest.step.details.description': 'Tell us the course type in detail and the milestone you want to reach.',
  'courseRequest.step.schedule.label': 'Step 3',
  'courseRequest.step.schedule.title': 'Share Your Time Zone and Available Times',
  'courseRequest.step.schedule.description': 'Post your lesson request, receive mentor invitations, and choose freely.',
  'courseRequest.step.upload.label': 'Step 4',
  'courseRequest.step.upload.title': 'Great. This Is the Final Step',
  'courseRequest.step.upload.description': 'Preview how your student information will appear to mentors, then upload course materials below.',
  'courseRequest.directionQuestion': 'Which course area best describes what you want to improve?',
  'courseRequest.courseTypeQuestion': 'Choose your course type',
  'courseRequest.saveExit': 'Save and Exit',
  'courseRequest.successTitle': 'Submitted',
  'courseRequest.successDesc': 'We have received your course request. You can contact a learning advisor in Help Center if needed.',
  'courseRequest.backHome': 'Back to Student Home',
  'courseRequest.fillAgain': 'Fill Again',
  'courseRequest.previewAria': 'Mentor-page card preview',
  'courseRequest.illustrationAria': 'Illustration area',
  'courseRequest.progressAria': 'Course request progress',
  'courseRequest.timeZoneFallback': 'Time zone',
  'courseRequest.estimatedHours': 'Estimated Hours: {hours}h',
  'courseRequest.courseFocus': 'Details: {value}',
  'courseRequest.goal': 'Goal: {value}',
  'courseRequest.earliestLesson': 'Earliest Lesson: {value}',
  'courseRequest.back': 'Back',
  'courseRequest.next': 'Next',
  'courseRequest.submit': 'Submit Request',
  'courseRequest.loading': 'Loading...',
  'courseRequest.loadFailed': 'Failed to load',
  'courseRequest.unsetStudentId': 'StudentID not set',
  'courseRequest.missingDegree': 'Degree not filled',
  'courseRequest.noDirection': 'No direction selected',
  'courseRequest.loadDraftFailed': 'Failed to load draft. Please try again later.',
  'courseRequest.unsupportedFiles': 'Unsupported files: {names}{suffix} (supported: {accept})',
  'courseRequest.moreFilesSuffix': ' and more',
  'courseRequest.requestIdFailed': 'Failed to get request ID',
  'courseRequest.uploadSignatureIncomplete': 'Upload signature response is incomplete',
  'courseRequest.uploadFailed': 'Upload failed',
  'courseRequest.saveDraftFailed': 'Save failed. Please try again later.',
  'courseRequest.submitFailed': 'Submit failed. Please try again later.',
  'courseRequest.details.totalHours': 'Estimated Course Hours',
  'courseRequest.details.hoursPlaceholder': 'e.g. 10',
  'courseRequest.details.hourUnit': 'h',
  'courseRequest.details.focus': 'Course and Request Details',
  'courseRequest.details.focusPlaceholder': 'e.g. I need help with Quiz 1 and Quiz 2 for Biomedical Engineering.\n\nI would like the mentor to explain the slides in more detail.',
  'courseRequest.details.milestone': 'Goal / Milestone',
  'courseRequest.details.milestonePlaceholder': 'e.g. Aim for an A on the final exam in 6 weeks',
  'courseRequest.upload.drop': 'Drop files',
  'courseRequest.upload.orUpload': 'or upload course materials',
  'courseRequest.upload.button': 'Upload',
  'courseRequest.upload.remove': 'Remove file',
  'courseRequest.upload.clear': 'Clear All',
  'courseRequest.schedule.chooseTime': 'Choose Available Times',
  'courseRequest.schedule.calendarAria': 'Available time calendar',
  'courseRequest.schedule.prevMonth': 'Previous month',
  'courseRequest.schedule.nextMonth': 'Next month',
  'courseRequest.schedule.duration': 'Session Duration',
  'courseRequest.schedule.durationAria': 'Session duration in hours',
  'courseRequest.schedule.mobile.timeZone': 'Time Zone',
  'courseRequest.schedule.mobile.availability': 'Available Times',
  'courseRequest.schedule.mobile.availabilityHint': 'Add multiple dates and time ranges',
  'courseRequest.schedule.mobile.add': 'Add',
  'courseRequest.schedule.mobile.date': 'Date',
  'courseRequest.schedule.mobile.start': 'Start',
  'courseRequest.schedule.mobile.end': 'End',
  'courseRequest.schedule.mobile.delete': 'Delete this time range',
  'courseRequest.schedule.mobile.emptyTitle': 'No available times added yet',
  'courseRequest.schedule.mobile.editTime': 'Edit Available Time',
  'courseRequest.schedule.mobile.addTime': 'Add Available Time',
  'courseRequest.schedule.weekday.sun': 'Sun',
  'courseRequest.schedule.weekday.mon': 'Mon',
  'courseRequest.schedule.weekday.tue': 'Tue',
  'courseRequest.schedule.weekday.wed': 'Wed',
  'courseRequest.schedule.weekday.thu': 'Thu',
  'courseRequest.schedule.weekday.fri': 'Fri',
  'courseRequest.schedule.weekday.sat': 'Sat',
  'courseRequest.selectPlaceholder': 'Select',
  'courseRequest.mockSchool.stanford': 'Stanford University',
  'courseRequest.mockSchool.tsinghua': 'Tsinghua University',
  'courseRequest.mockSchool.mit': 'MIT',
  'courseRequest.mockSchool.peking': 'Peking University',
  'courseRequest.mockSchool.toronto': 'University of Toronto',
  'courseRequest.mockSchool.nus': 'National University of Singapore',

  'listings.emptyMentors': 'No mentors yet',
  'listings.emptyRequests': 'No student requests yet',
  'listings.actionFailed': 'Action failed. Please try again later.',
  'listings.openMentorProfile': 'Open mentor profile in a new page: {name}',
  'listings.openRequestDetail': 'Open course request in a new tab: {name}',
  'footer.aria': 'Mentory footer',
  'footer.brandAria': 'Mentory',
  'footer.description': 'Make learning go further.',
  'footer.support': 'Support',
  'footer.helpCenter': 'Help Center',
  'footer.contactUs': 'Contact us',
  'footer.about': 'About us',
  'footer.introduction': 'Introduction',
  'footer.mentorOpportunities': 'Mentor opportunities',
  'footer.businessCooperation': 'Business partnerships',
  'footer.rules': 'Rules',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'footer.refundRules': 'Refund / cancellation rules',
  'footer.copyright': '© 2026 Mentory',
  'listings.favorite': 'Favorite',
  'listings.unfavorite': 'Remove favorite',
  'listings.reviews': '{count} reviews',
  'listings.hours': '{hours}h',
  'listings.details': 'Details: {value}',
  'listings.goal': 'Goal: {value}',
  'listings.city.auckland': 'Auckland',
  'listings.city.solomon': 'Solomon Islands',
  'listings.city.brisbane': 'Brisbane',
  'listings.city.tokyo': 'Tokyo',
  'listings.city.shanghai': 'Shanghai',
  'listings.city.bangkok': 'Bangkok',
  'listings.city.dhaka': 'Dhaka',
  'listings.city.karachi': 'Karachi',
  'listings.city.dubai': 'Dubai',
  'listings.city.moscow': 'Moscow',
  'listings.city.johannesburg': 'Johannesburg',
  'listings.city.berlin': 'Berlin',
  'listings.city.london': 'London',
  'listings.city.losAngeles': 'Los Angeles',
  'listings.city.california': 'California',
  'listings.city.chicago': 'Chicago',
  'listings.city.newYork': 'New York',
  'listings.city.halifax': 'Halifax',
  'listings.city.saoPaulo': 'Sao Paulo',
  'listings.language.zh': 'Chinese',
  'listings.language.en': 'English',
  'listings.language.ja': 'Japanese',
  'listings.language.de': 'German',
  'listings.language.fr': 'French',
  'listings.language.es': 'Spanish',
  'mentorPage.forbidden': 'Mentors only. Please log in or register with a mentor account.',
  'mentorPage.pendingTitle': 'You are ready',
  'mentorPage.pendingSubtitle': 'Thanks for joining us. We will unlock your mentor account soon and notify you when everything is complete.',
  'mentorProfileEditor.saveAndExit': 'Save and Exit',
  'mentorProfileEditor.saved': 'Saved',
  'mentorProfileEditor.title': 'Edit Profile Card',
  'mentorProfileEditor.changeAvatar': 'Change avatar',
  'mentorProfileEditor.avatarAlt': 'Avatar',
  'mentorProfileEditor.avatarPlaceholder': 'Avatar',
  'mentorProfileEditor.name': 'Name',
  'mentorProfileEditor.namePlaceholder': 'Mentor name',
  'mentorProfileEditor.gender': 'Gender',
  'mentorProfileEditor.gender.male': 'Male',
  'mentorProfileEditor.gender.female': 'Female',
  'mentorProfileEditor.degree': 'Degree',
  'mentorProfileEditor.school': 'School',
  'mentorProfileEditor.schoolPlaceholder': 'Optional',
  'mentorProfileEditor.timeZone': 'Time zone',
  'mentorProfileEditor.courses': 'Courses you can teach',
  'mentorProfileEditor.coursesPlaceholder': 'Python programming, Machine Learning, Deep Learning',
  'mentorProfileEditor.addTeachingLanguages': 'Add teaching languages',
  'mentorProfileEditor.editTeachingLanguages': 'Edit teaching languages',
  'mentorProfileEditor.selectTeachingLanguages': 'Select teaching languages',
  'mentorProfileEditor.selectPlaceholder': 'Select',
  'mentorProfileEditor.avatarTooLarge': 'Avatar image must be 5 MB or smaller',
  'mentorProfileEditor.imageOnly': 'Only image files are supported',
  'mentorProfileEditor.uploadingAvatar': 'Avatar is uploading. Please save after it finishes.',
  'mentorProfileEditor.editUnavailable': 'Profile card cannot be edited now',
  'mentorProfileEditor.mentorPendingCannotEdit': 'Mentor account is under review. Profile editing is unavailable.',
  'mentorProfileEditor.loadFailed': 'Failed to load. Please try again later.',
  'mentorProfileEditor.uploading': 'Uploading...',
  'mentorProfileEditor.signatureIncomplete': 'Upload signature response is incomplete',
  'mentorProfileEditor.uploadFailed': 'Upload failed',
  'mentorProfileEditor.uploadFailedRetry': 'Upload failed. Please try again later.',
  'mentorProfileEditor.ossCorsUploadFailed': 'Upload failed. Please check the OSS CORS configuration.',
  'mentorProfileEditor.saveFailed': 'Save failed. Please try again later.',
  'mentorProfileEditor.previewName': 'Mentor name',
  'mentorProfileEditor.lang.zh': 'Chinese',
  'mentorProfileEditor.lang.en': 'English',
  'mentorProfileEditor.lang.ja': 'Japanese',
  'mentorProfileEditor.lang.ko': 'Korean',
  'mentorProfileEditor.lang.fr': 'French',
  'mentorProfileEditor.lang.es': 'Spanish',
  'mentorProfileEditor.lang.de': 'German',
  'mentorProfileEditor.lang.it': 'Italian',
  'mentorDetail.rating.clarity': 'Clear explanation',
  'mentorDetail.rating.communication': 'Smooth communication',
  'mentorDetail.rating.preparation': 'Well prepared',
  'mentorDetail.rating.expertise': 'Professional knowledge',
  'mentorDetail.rating.punctuality': 'Punctual',
  'mentorDetail.showMore': 'Show more',
  'mentorDetail.close': 'Close',
  'mentorDetail.missingMentorId': 'Missing MentorID',
  'mentorDetail.notFound': 'Mentor not found',
  'mentorDetail.loadFailed': 'Failed to load. Please try again later.',
  'mentorDetail.mentorFallback': 'Mentor',
  'mentorDetail.selectCourseRequired': 'Please choose a course',
  'mentorDetail.sendFailed': 'Failed to send. Please try again later.',
  'mentorDetail.infoAndScheduleAria': 'Mentor information and schedule',
  'mentorDetail.previewAria': 'Mentor preview card',
  'mentorDetail.scheduleAria': 'Mentor available times',
  'mentorDetail.timeZonePrefix': 'Time zone:',
  'mentorDetail.chooseAppointmentTime': 'Choose appointment time',
  'mentorDetail.selectedAppointmentTime': 'Appointment time selected',
  'mentorDetail.expandScheduleHint': 'View the mentor calendar and available times',
  'mentorDetail.closeSchedule': 'Close appointment time picker',
  'mentorDetail.chooseDateAria': 'Choose date',
  'mentorDetail.calendarAria': 'Available date calendar',
  'mentorDetail.chooseTimeAria': 'Choose time',
  'mentorDetail.sendAppointmentAria': 'Send appointment',
  'mentorDetail.prevDay': 'Previous day',
  'mentorDetail.nextDay': 'Next day',
  'mentorDetail.clearSelection': 'Clear selection',
  'mentorDetail.clear': 'Clear',
  'mentorDetail.myAvailability': 'My availability',
  'mentorDetail.mentorAvailability': 'Mentor availability',
  'mentorDetail.sendAppointment': 'Send Appointment',
  'mentorDetail.ratingAria': 'Ratings and reviews',
  'mentorDetail.ratingSubtitle': 'Based on {count} reviews',
  'mentorDetail.noRatingData': 'No rating data yet',
  'mentorDetail.ratingDistributionAria': 'Rating distribution',
  'mentorDetail.overallRating': 'Overall rating',
  'mentorDetail.ratingCategoriesAria': 'Rating dimensions',
  'mentorDetail.reviewsAria': 'Student review list',
  'mentorDetail.emptyReviews': 'No written reviews yet',
  'mentorDetail.studentFallback': 'Student',
  'mentorDetail.weekday.sun': 'Sun',
  'mentorDetail.weekday.mon': 'Mon',
  'mentorDetail.weekday.tue': 'Tue',
  'mentorDetail.weekday.wed': 'Wed',
  'mentorDetail.weekday.thu': 'Thu',
  'mentorDetail.weekday.fri': 'Fri',
  'mentorDetail.weekday.sat': 'Sat',
  'requestDetail.missingId': 'Missing request ID',
  'requestDetail.mentorLoginRequired': 'Please log in with a mentor account to view this',
  'requestDetail.notFound': 'Request not found',
  'requestDetail.mentorPending': 'Mentor account is under review. This page is unavailable.',
  'requestDetail.mentorOnly': 'Mentors only',
  'requestDetail.loadFailed': 'Failed to load. Please try again later.',
  'requestDetail.attachmentFallback': 'Attachment {index}',
  'requestDetail.missingRequestId': 'Missing requestId',
  'requestDetail.invalidSignedUrl': 'Invalid signed URL',
  'requestDetail.downloadFailed': 'Download failed. Please try again later.',
  'requestDetail.noDownloadUrl': 'No download link was returned',
  'requestDetail.infoAndScheduleAria': 'Course information and schedule',
  'requestDetail.previewAria': 'Course preview card',
  'requestDetail.scheduleAria': 'Available times',
  'requestDetail.expandScheduleHint': 'View the student calendar and available times',
  'requestDetail.studentAvailability': 'Student availability',
  'requestDetail.attachmentsAria': 'Student uploaded attachments',
  'requestDetail.attachmentsTitle': 'Student Attachments',
  'requestDetail.downloadAll': 'Download All',
  'requestDetail.downloadFile': 'Download {fileName}',
  'requestDetail.unavailable': 'Unavailable',
  'requestDetail.emptyAttachments': 'No attachments uploaded',
  'requestDetail.confirmSendTitle': 'Send appointment?',
  'requestDetail.noSelectedTime': 'No appointment time selected',
  'requestDetail.selectedTime': 'Selected time: {value}',
  'requestDetail.confirmSend': 'Send',
  'requestDetail.sending': 'Sending...',
  'classroom.title': 'Classroom',
  'classroom.observerTitle': 'Classroom Observation',
  'classroom.observerReadonlyNotice': 'Platform observation mode: you can view the same classroom as the student and mentor, but chat and classroom controls are read-only.',
  'classroom.observerPresentNotice': 'A Mentory team member is observing this lesson',
  'classroom.observerReadonly': 'Chat is read-only in observation mode',
  'classroom.observerChatPlaceholder': 'Chat is read-only in observation mode',
  'classroom.observerWaitingStreams': 'Observing. Waiting for classroom video...',
  'classroom.observerWatching': 'Observing',
  'classroom.observerMissingStreams': 'The observation authorization is missing classroom playback URLs',
  'classroom.observerJoinAuthMissing': 'The observation authorization is missing the room join URL',
  'classroom.observerJoiningRoom': 'Observing. Joining the classroom...',
  'classroom.observerJoinUnsupported': 'The current live video SDK does not support joining as an observer',
  'classroom.observerSignalActivationFailed': 'Failed to open the observer signaling channel',
  'classroom.observerConnecting': 'Observing. Connecting to classroom video...',
  'classroom.studentVideo': 'Student Video',
  'classroom.mentorVideo': 'Mentor Video',
  'classroom.waitingStudentVideo': 'Waiting for student video...',
  'classroom.waitingMentorVideo': 'Waiting for mentor video...',
  'classroom.student': 'Student',
  'classroom.mentor': 'Mentor',
  'classroom.unknownSender': 'Unknown',
  'classroom.recordingActive': 'Recording',
  'classroom.recordingStarting': 'Starting recording...',
  'classroom.recordingFailed': 'Failed to start recording',
  'classroom.recordingStartFailed': 'Failed to start recording',
  'classroom.recordingWaiting': 'Waiting for the other participant to confirm recording',
  'classroom.recordingDisabled': 'This lesson is not being recorded',
  'classroom.recordingConsentTitle': 'Confirm recording before entering',
  'classroom.recordingConsentScope': 'This classroom will record audio, video, screen sharing, and classroom comments',
  'classroom.recordingConsentAccess': 'Recordings are available only to authorized lesson participants and platform personnel when necessary.',
  'classroom.recordingConsentChoice': 'If either participant declines, the lesson remains available but cloud recording will not start.',
  'classroom.recordingConsentPolicyPrefix': 'For detailed handling rules, see ',
  'classroom.recordingConsentPolicyLink': 'Privacy Policy—Classroom Recording',
  'classroom.recordingConsentPolicySuffix': '',
  'classroom.recordingConsentPolicyJoin': 'and',
  'classroom.recordingConsentClose': 'Exit',
  'classroom.recordingConsentReturn': 'Exit',
  'classroom.enterWithoutRecording': 'Do not record this lesson',
  'classroom.acceptRecording': 'Agree to recording and enter classroom',
  'classroom.savingRecordingConsent': 'Saving...',
  'classroom.recordingConsentLoadFailed': 'Unable to load the recording notice. Please try again.',
  'classroom.recordingConsentSaveFailed': 'Unable to save your recording choice. Please try again.',
  'classroom.localVideo': 'My video',
  'classroom.remoteVideo': 'Other video',
  'classroom.openMic': 'Turn on mic',
  'classroom.closeMic': 'Turn off mic',
  'classroom.openCamera': 'Turn on camera',
  'classroom.closeCamera': 'Turn off camera',
  'classroom.shareScreen': 'Share screen',
  'classroom.stopShare': 'Stop sharing',
  'classroom.unsupportedScreenShare': 'This browser does not support screen sharing',
  'classroom.scheduleUnavailable': 'Next lesson scheduling is unavailable for this classroom',
  'classroom.scheduleNext': 'Schedule Next Lesson',
  'classroom.leave': 'Leave Classroom',
  'classroom.end': 'End Classroom',
  'classroom.chat': 'Chat',
  'classroom.tempFilesReady': 'Temporary classroom files are ready to clean up',
  'classroom.loadingChat': 'Loading chat history...',
  'classroom.me': 'Me',
  'classroom.unnamedFile': 'Unnamed file',
  'classroom.fileCleaned': 'File cleaned',
  'classroom.preparingDownload': 'Preparing download...',
  'classroom.downloadFile': 'Download file',
  'classroom.chatClosedPlaceholder': 'Classroom has ended. Chat is read-only.',
  'classroom.chatPlaceholder': 'Type a classroom message. Press Enter to send, Shift + Enter for a new line',
  'classroom.uploading': 'Uploading...',
  'classroom.uploadFile': 'Upload file',
  'classroom.sending': 'Sending...',
  'classroom.sendMessage': 'Send Message',
  'classroom.submitActualHours': 'Submit actual hours for this lesson',
  'classroom.appointmentReminder': 'Next lesson appointment reminder',
  'classroom.reminder': 'Classroom Reminder',
  'classroom.appointmentFrom': '{name} sent a next lesson appointment',
  'classroom.closeReminder': 'Close reminder',
  'classroom.accept': 'Accept',
  'classroom.reject': 'Reject',
  'classroom.viewMessages': 'View in Messages',
  'classroom.prevDay': 'Previous day',
  'classroom.nextDay': 'Next day',
  'classroom.syncingAvailability': 'Syncing both sides’ availability...',
  'classroom.availabilityHint': 'Click a time slot or drag the selected range to adjust',
  'classroom.myAvailability': 'My availability',
  'classroom.otherAvailability': 'Other party availability',
  'classroom.sendAppointment': 'Send Appointment',
  'classroom.pendingStatus': 'Preparing to enter classroom...',
  'classroom.remoteFallback': 'Other party',
  'classroom.localCameraOff': 'Camera off',
  'classroom.screenSharingActive': '{name} is sharing screen',
  'classroom.screenSharingWaiting': 'Waiting for {name}’s shared screen...',
  'classroom.noScreenShare': 'Screen sharing has not started',
  'classroom.bothJoined': 'Both sides have entered the classroom',
  'classroom.waitingRemoteVideo': 'Both sides have entered. Waiting for {name}’s video...',
  'classroom.waitingRemoteJoin': 'Entered classroom. Waiting for {name} to join...',
  'classroom.appointmentSent': 'Next lesson appointment sent',
  'classroom.appointmentSendFailed': 'Failed to send next lesson appointment. Please try again later.',
  'classroom.appointmentAccepted': 'Next lesson appointment accepted',
  'classroom.appointmentRejected': 'Next lesson appointment rejected',
  'classroom.appointmentUpdateFailed': 'Failed to update appointment status. Please try again later.',
  'classroom.ossCorsUploadFailed': 'Upload failed. Please check the OSS CORS configuration.',
  'classroom.syncAppointmentFailed': 'Failed to sync appointment status. Please try again later.',
  'classroom.chatLoadFailed': 'Failed to load classroom chat. Please try again later.',
  'classroom.markCleanupFailed': 'Failed to mark temporary classroom files for cleanup. Please try again later.',
  'classroom.localPreviewFailed': 'Local camera preview failed to initialize',
  'classroom.pushNotReady': 'Classroom streaming is not ready yet',
  'classroom.runtimeError': 'Classroom connection error. Please try again later.',
  'classroom.micPermissionError': 'Microphone access failed. Check browser permissions.',
  'classroom.micDeviceError': 'Microphone unavailable. Check its connection or whether another app is using it.',
  'classroom.audioDeviceError': 'Audio device unavailable. Check your system audio devices.',
  'classroom.cameraPermissionError': 'Camera access failed. Check browser permissions.',
  'classroom.cameraDeviceError': 'Camera unavailable. Check its connection or whether another app is using it.',
  'classroom.screenDeviceError': 'Screen sharing encountered a device error. Start sharing again.',
  'classroom.localDeviceError': 'Local media device error. Check your devices and try again.',
  'classroom.invalidCourseId': 'Invalid course ID',
  'classroom.requestingAuth': 'Requesting classroom authorization...',
  'classroom.invalidAuth': 'Classroom authorization response is invalid',
  'classroom.loadingSdk': 'Loading live video SDK...',
  'classroom.browserUnsupported': 'This browser does not support live video{reason}',
  'classroom.initializingDevices': 'Initializing local devices...',
  'classroom.sdkInternalError': 'Live video SDK internal error. Please re-enter the classroom.',
  'classroom.networkReconnecting': 'Network changed. Reconnecting to classroom...',
  'classroom.reconnecting': 'Reconnecting classroom...',
  'classroom.reconnectFailed': 'Classroom reconnect failed. Check your network and re-enter.',
  'classroom.disconnected': 'Classroom disconnected',
  'classroom.entering': 'Entering classroom...',
  'classroom.mediaOnlineFailed': 'Classroom media failed to go online. Try re-entering.',
  'classroom.enterFailed': 'Failed to enter classroom. Please try again later.',
  'classroom.remoteVideoFetchFailed': 'Failed to load other party video',
  'classroom.micSwitchFailed': 'Failed to switch microphone',
  'classroom.micControlUnavailable': 'Microphone control is not supported by the current real-time audio/video SDK',
  'classroom.cameraSwitchFailed': 'Failed to switch camera',
  'classroom.sdkUnsupportedScreenShare': 'The current live video SDK does not support screen sharing',
  'classroom.sharingScreen': 'Sharing screen',
  'classroom.shareFailed': 'Screen sharing failed',
  'classroom.chatSendFailed': 'Failed to send classroom message. Please try again later.',
  'classroom.invalidClassroomId': 'Invalid classroom ID',
  'classroom.uploadSignatureIncomplete': 'Upload signature response is incomplete',
  'classroom.uploadFailed': 'Upload failed',
  'classroom.unsupportedFiles': 'Unsupported files: {names}{suffix} (supported: {accept})',
  'classroom.moreFilesSuffix': ' and more',
  'classroom.fileUploadFailed': 'Failed to upload classroom file. Please try again later.',
  'classroom.downloadLinkFailed': 'Failed to generate download link',
  'classroom.getDownloadLinkFailed': 'Failed to get file download link. Please try again later.',
  'classroom.leaving': 'Leaving classroom...',
  'classroom.invalidEndHours': 'Enter valid lesson hours in 0.25-hour increments',
  'classroom.ending': 'Ending classroom...',
  'classroom.endFailed': 'Failed to end classroom. Please try again later.',
  'timezoneModal.title': 'Search by Region',
  'timezoneModal.random': 'Explore',
  'timezoneModal.europe': 'Europe',
  'timezoneModal.northAmerica': 'North America',
  'timezoneModal.oceania': 'Oceania',
  'timezoneModal.japanKorea': 'Japan / Korea',
  'timezoneModal.china': 'China',

  'favorites.title': 'Favorites',
  'favorites.heroStudent': 'Student and mentor favorites are separate. Organize mentor groups by course direction or goal.',
  'favorites.heroMentor': 'Mentor and student favorites are separate. Organize student groups here.',
  'favorites.recent': 'Recent Visits',
  'favorites.today': 'Today',
  'favorites.yesterday': 'Yesterday',
  'favorites.noRecent': 'No recent visits',
  'favorites.recentDesc': 'Recently viewed favorites stay here so you can return to where you left off',
  'favorites.newCollection': 'New collection',
  'favorites.createdAt': 'Created {date}',
  'favorites.systemDefault': 'System default',
  'favorites.loginRequiredCollections': 'Please log in to view favorite collections',
  'favorites.loginRequiredFavorites': 'Please log in to view favorites',
  'favorites.invalidCollectionId': 'Favorite collection ID is invalid',
  'favorites.noPermissionCollections': 'This account does not have access to favorite collections',
  'favorites.noPermissionCollection': 'This account does not have access to this favorite collection',
  'favorites.collectionNotFound': 'Favorite collection not found',
  'favorites.loadCollectionsFailed': 'Failed to load favorite collections. Please try again later.',
  'favorites.loadFailed': 'Failed to load. Please try again later.',
  'favorites.deleteFailed': 'Delete failed. Please try again later.',
  'favorites.createFailed': 'Create failed. Please try again later.',
  'favorites.nameRequired': 'Enter a collection name',
  'favorites.loginBeforeCreate': 'Please log in before creating a collection',
  'favorites.loadingCollections': 'Loading favorite collections',
  'favorites.removeFavorite': 'Remove favorite',
  'favorites.emptyCollection': 'Empty collection',
  'favorites.coverAlt': '{title} cover {index}',
  'favorites.createNewTitle': 'Create New Collection',
  'favorites.createStudentDesc': 'Organize student groups by course direction, student profile, or goal.',
  'favorites.createMentorDesc': 'Organize mentor groups by course direction, mentor style, or goal.',
  'favorites.newFavorite': 'New Favorite',
  'favorites.deleteTitle': 'Delete this collection?',
  'favorites.deleteDesc': '"{title}" will be permanently deleted',
  'favorites.createTitle': 'New Collection',
  'favorites.namePlaceholder': 'Name',
  'favorites.back': 'Back to favorites',
  'favorites.empty': 'No favorites yet',
  'favorites.bulkActions': 'Bulk actions',
  'favorites.selectedCount': '{count} selected',
  'favorites.unfavorite': 'Unfavorite',
  'favorites.moveTo': 'Move to...',
  'favorites.moveTitle': 'Move to Collection',
  'favorites.noOtherCollections': 'No other collections. Create one from the favorites page first.',
  'favorites.chooseTarget': 'Choose a collection',
  'favorites.chooseCollection': 'Choose collection',
  'favorites.moveFailed': 'Move failed. Please try again later.',
  'favorites.moveTargetRequired': 'Choose a target collection',
  'favorites.moveFailedCount': '{count} items failed to unfavorite. Please try again later.',
  'favorites.moving': 'Moving...',
  'favorites.move': 'Move',
  'favorites.defaultCollection': 'Default Collection',
  'favorites.defaultCollectionSpaced': 'Default Collection',

  'recent.older': 'Earlier',
  'recent.title': 'Recent Visits',
  'recent.edit': 'Edit',
  'recent.removeRecord': 'Remove this record',
  'recent.loginRequired': 'Please log in to view recent visits',
  'recent.noPermission': 'This account does not have access to recent visits',
  'recent.loadFailed': 'Failed to load recent visits. Please try again later.',
  'recent.deleteFailed': 'Delete failed. Please try again later.',
  'recent.mentorEmpty': 'No student visit records yet',
  'recent.studentEmpty': 'No mentor visit records yet',

  'courseOnboarding.title': 'Complete Course Details',
  'courseOnboarding.close': 'Close',
  'courseOnboarding.chooseCourse': 'Choose course',
  'courseOnboarding.loading': 'Loading...',
  'courseOnboarding.empty': 'No courses yet',
  'courseOnboarding.otherDirection': 'Other Course Direction',
  'courseOnboarding.unknownType': 'Unknown Type',
  'courseOnboarding.deleteFailed': 'Delete failed. Please try again later.',
  'courseOnboarding.deleteDraft': 'Delete draft',
  'courseOnboarding.deleteSubmitted': 'Delete submitted request',
  'courseOnboarding.confirmDeleteDraftTitle': 'Delete course draft?',
  'courseOnboarding.confirmDeleteSubmittedTitle': 'Delete course request?',
  'courseOnboarding.confirmDeleteDraftDescription': 'This cannot be undone.',
  'courseOnboarding.confirmDeleteSubmittedDescription': 'It will no longer be shown to mentors and cannot be restored.',
  'courseOnboarding.unpublished': 'Unpublished',
  'courseOnboarding.createdAt': 'Created {date}',
  'courseOnboarding.delete': 'Delete',
  'courseOnboarding.createEntryAria': 'Create course entry',
  'courseOnboarding.startCreating': 'Start Creating a New Course',
  'courseOnboarding.createNew': 'Create New Course',

  'wallet.title': 'Wallet',
  'wallet.loginRequired': 'Please log in to view your wallet',
  'wallet.summaryAria': 'Wallet overview',
  'wallet.balanceAria': 'Balance',
  'wallet.remainingHours': 'Remaining Hours',
  'wallet.hours': 'hours',
  'wallet.monthSpending': 'This Month',
  'wallet.monthNetSpending': 'Net Spending This Month',
  'wallet.totalTopUp': 'Total Top-up',
  'wallet.topUp': 'Top Up',
  'wallet.topUpMethod': 'Top-up method',
  'wallet.topUpHours': 'Top-up hours',
  'wallet.duration': 'Top-up duration',
  'wallet.chooseHours': 'Choose hours',
  'wallet.quickHours': 'Quick hours',
  'wallet.enterHours': 'Enter hours',
  'wallet.priceDetails': 'Price details',
  'wallet.lessThan10': 'Top-up duration < 10 hours',
  'wallet.atLeast10': 'Top-up duration >= 10 hours',
  'wallet.price799': 'CNY 799/hour',
  'wallet.price699': 'CNY 699/hour',
  'wallet.total': 'Total',
  'wallet.topUpNow': 'Top Up Now',
  'wallet.localOrderCreating': 'Creating order...',
  'wallet.localOrderCreateFailed': 'Failed to create the top-up order. Please try again later.',
  'wallet.paymentReferenceUnavailable': 'Unable to load the order note',
  'wallet.copyPaymentReference': 'Copy order note',
  'wallet.open127': 'Open with 127.0.0.1',
  'wallet.paypalUnavailable': 'PayPal is unavailable',
  'wallet.tips': 'Tips',
  'wallet.tipRealtimePayPal': 'Balance updates immediately after a successful top-up. Overseas Visa, Mastercard, and other cards can also pay through PayPal as a guest.',
  'wallet.tipNonRealtimeLocal': 'After a successful top-up, balance updates are not immediate. Mentory is still improving the payment experience.',
  'wallet.tipHelp': 'For payment issues, contact us from Help Center',
  'wallet.paySuccess': 'Payment Successful',
  'wallet.fxInvalid': 'FX quote data is invalid. Please try again.',
  'wallet.fxFailed': 'Failed to get FX quote. Please try again later.',
  'wallet.localhostUnsupported': 'PayPal sandbox client token does not support localhost. Open this page with 127.0.0.1.',
  'wallet.paypalInitFailed': 'PayPal initialization failed. Please try again later.',
  'wallet.invalidHours': 'Enter a valid number of hours',
  'wallet.paypalTryLater': 'PayPal is unavailable. Please try again later.',
  'wallet.fxLoading': 'Getting live FX quote. Please try again shortly.',
  'wallet.noFxQuote': 'No FX quote is available. Please try again later.',
  'wallet.fxExpiredRefreshing': 'FX quote expired. Refreshing the latest quote.',
  'wallet.paypalNotReady': 'PayPal is not ready yet. Please try again shortly.',
  'wallet.redirectingPayPal': 'Redirecting to PayPal...',
  'wallet.fxUpdatedContinue': 'FX quote updated. Continuing with the latest quote.',
  'wallet.paymentCompletedStatus': 'Payment completed. Status: {status}',
  'wallet.captureFailed': 'Payment approved, but capture failed. Please check your balance later.',
  'wallet.paymentCanceled': 'Payment canceled',
  'wallet.paymentFailed': 'Payment failed. Please try again later.',
  'wallet.fxExpiredClickAgain': 'FX quote expired. Refreshing the latest quote. Please click pay again.',
  'wallet.fxInvalidClickAgain': 'FX quote is invalid. Refreshing the latest quote. Please click pay again.',
  'wallet.paymentInitFailed': 'Payment initialization failed. Please try again later.',
  'wallet.paypalDesc': 'Supports international cards and balance',
  'wallet.alipay': 'Alipay',
  'wallet.alipayDesc': 'Recommended for users in China',
  'wallet.alipayTransferTitle': 'Alipay Transfer',
  'wallet.alipayTransferSubtitle': 'Open Alipay, search for the Mentory business account, and complete the transfer',
  'wallet.alipayAccount': 'Open Alipay and search',
  'wallet.alipayAmount': 'Amount due',
  'wallet.alipayRemark': 'Transfer note (Important)',
  'wallet.alipayCopy': 'Copy',
  'wallet.alipayCopied': 'Copied',
  'wallet.alipayCopyAccount': 'Copy Alipay account',
  'wallet.alipayCopyStudentId': 'Copy order note',
  'wallet.alipayStudentIdUnavailable': 'Unable to load the order note',
  'wallet.alipayPaymentNotice': 'Mentory will update your lesson hours after confirming receipt of payment',
  'wallet.alipayPaid': 'I Have Paid',
  'wallet.alipayReporting': 'Submitting...',
  'wallet.alipayReportSubmitted': 'Payment Report Submitted',
  'wallet.alipayReportSuccess': 'Your lesson hours will be added after Mentory confirms receipt',
  'wallet.alipayReportFailed': 'Failed to submit payment information. Please try again later.',
  'wallet.alipayClose': 'Close Alipay transfer instructions',
  'wallet.wechat': 'WeChat Pay',
  'wallet.wechatDesc': 'Pay with Mentory’s official merchant QR code',
  'wallet.wechatPaymentDialog': 'WeChat Pay',
  'wallet.wechatQrAlt': 'WeChat merchant payment QR code for Mentory’s Store',
  'wallet.wechatSaveQr': 'Save QR Code',
  'wallet.wechatAmount': 'Amount due',
  'wallet.wechatExpectedMerchant': 'Verify the payee',
  'wallet.wechatMerchantName': 'Mentory’s Store',
  'wallet.wechatRemark': 'Payment note (Important)',
  'wallet.wechatStudentIdUnavailable': 'Unable to load the order note',
  'wallet.wechatCopyStudentId': 'Copy order note',
  'wallet.wechatCopy': 'Copy',
  'wallet.wechatCopied': 'Copied',
  'wallet.wechatReporting': 'Submitting...',
  'wallet.wechatPaid': 'I Have Paid',
  'wallet.wechatReportSubmitted': 'Payment Details Submitted',
  'wallet.wechatReportSuccess': 'Your lesson hours will be added after Mentory confirms receipt',
  'wallet.wechatReportFailed': 'Failed to submit payment details. Please try again later.',
  'wallet.wechatClose': 'Close WeChat Pay instructions',
  'wallet.selectedFallback': 'Selected method',
  'wallet.selectedMethod': 'Selected {method}, hours {hours}, unit price CNY {unitPrice}/hour, total CNY {total}. Top-up is under development.',
  'wallet.refundAction': 'Request Refund',
  'wallet.refundTitle': 'Request a Refund',
  'wallet.refundLoadFailed': 'Failed to load refund information. Please try again later.',
  'wallet.refundQuoteFailed': 'Failed to calculate the refund. Adjust the hours and try again.',
  'wallet.refundSubmitFailed': 'Failed to submit the refund. Please try again later.',
  'wallet.refundChooseOrder': 'Choose a top-up',
  'wallet.refundOrderSummary': 'Purchased {hours} hours · Paid CNY {amount}',
  'wallet.refundConsumedOrderSummary': 'Purchased {hours} hours / {available} hours refundable · Paid CNY {amount}',
  'wallet.refundNoOrders': 'No refundable unused hours',
  'wallet.refundHours': 'Refund hours',
  'wallet.refundAll': 'Refund all available',
  'wallet.refundQuarterHint': 'Refunds use 0.25-hour increments. Tier pricing is recalculated after a partial refund.',
  'wallet.refundCalculating': 'Calculating refund...',
  'wallet.refundReferenceCny': 'CNY reference refund',
  'wallet.refundOriginalAmount': 'Returned to original payment',
  'wallet.refundOriginalAmountNonRealtime': 'Returned to original payment (not real-time)',
  'wallet.refundUsedHours': 'Hours used',
  'wallet.refundBalanceAfter': 'Wallet balance after refund',
  'wallet.refundScheduleWarning': 'You have about {hours} hours of upcoming lessons. Your balance may be insufficient after this refund, and you may need to top up before entering class',
  'wallet.refundHistory': 'Refund history',
  'wallet.refundCompleted': 'Refund completed',
  'wallet.refundFailed': 'Refund failed',
  'wallet.refundPending': 'Refund pending',
  'wallet.refundProcessing': 'Confirming',
  'wallet.refundSubmitting': 'Submitting...',
  'wallet.refundConfirmSubmit': 'Confirm Refund',
  'wallet.refundSuccess': 'Refund Submitted',
  'wallet.refundSuccessDescription': 'Funds will be returned according to the processing status of the original payment method.',

  'messages.title': 'Messages',
  'messages.loginRequired': 'Please log in to view messages',
  'messages.loadThreadsFailed': 'Failed to load conversations. Please try again later.',
  'messages.schedule': 'Schedule',
  'messages.starFailed': 'Failed to star. Please try again later.',
  'messages.unstarFailed': 'Failed to unstar. Please try again later.',
  'messages.archiveFailed': 'Failed to archive conversation. Please try again later.',
  'messages.shellAria': 'Messages list and details',
  'messages.backToConversations': 'Back to conversations',
  'messages.recent': 'Recent',
  'messages.conversations': 'Conversations',
  'messages.conversationCount': '{count} conversations',
  'messages.me': 'Me',
  'messages.otherParty': 'Other party',
  'messages.starred': 'Starred',
  'messages.unreadThread': 'Unread messages in this conversation',
  'messages.moreActions': 'More actions',
  'messages.star': 'Star',
  'messages.unstar': 'Unstar',
  'messages.archive': 'Archive',
  'messages.scheduleNextLesson': 'Schedule next lesson',
  'messages.reschedule': 'Reschedule',
  'messages.prevDay': 'Previous day',
  'messages.nextDay': 'Next day',
  'messages.close': 'Close',
  'messages.myAvailability': 'My availability',
  'messages.otherAvailability': 'Other party availability',
  'messages.sendAppointment': 'Send Appointment',
  'messages.selectConversation': 'Select a conversation on the left to view details',
  'messages.noConversations': 'No conversations yet',
  'messages.today': 'Today',
  'messages.yesterday': 'Yesterday',
  'messages.weekday.sun': 'Sun',
  'messages.weekday.mon': 'Mon',
  'messages.weekday.tue': 'Tue',
  'messages.weekday.wed': 'Wed',
  'messages.weekday.thu': 'Thu',
  'messages.weekday.fri': 'Fri',
  'messages.weekday.sat': 'Sat',
  'messages.dateTime.monthDay': '{month}/{day} {time}',
  'messages.dateTime.fullDate': '{year}/{month}/{day} {label}',
  'messages.dateTime.window': '{month}/{day} {weekday} {start}-{end} ({timezone})',

  'appointment.onlyRecallOwn': 'Only schedules you sent can be recalled',
  'appointment.expiredCannotRecall': 'Schedule expired and cannot be recalled',
  'appointment.respondedCannotRecall': 'The other party has responded, so this cannot be recalled',
  'appointment.accept': 'Accept',
  'appointment.reject': 'Reject',
  'appointment.cancelled': 'Cancel',
  'appointment.reschedule': 'Reschedule',
  'appointment.changeStatusTo': 'Change schedule status to',
  'appointment.moreActions': 'More actions',
  'appointment.viewRequestDetails': 'Course request details',
  'appointment.viewRequestDetailsAria': 'View course request details for {course}',
  'appointment.scheduleNextLesson': 'Schedule next lesson',
  'appointment.cancelCourse': 'Cancel lesson',
  'appointment.notHeld': 'This lesson was not held',
  'appointment.confirmNotHeld': 'Confirm lesson was not held',
  'appointment.classWasHeld': 'Lesson was held',
  'appointment.withdrawNotHeld': 'Withdraw no-lesson confirmation',
  'appointment.cancelConfirm': 'Cancel this lesson? No lesson hours will be deducted.',
  'appointment.cancelDialogTitle': 'Cancel lesson',
  'appointment.cancelDialogMessage': 'Are you sure you want to cancel this lesson? No lesson hours will be deducted',
  'appointment.cancelDialogConfirm': 'Confirm',
  'appointment.cancelling': 'Cancelling…',
  'appointment.notHeldDialogMessage': 'Start a no-lesson confirmation? The lesson will only be removed without deducting lesson hours after the other party confirms',
  'appointment.confirmNotHeldDialogMessage': 'Confirm that this lesson did not take place? The lesson will be removed and no lesson hours will be deducted',
  'appointment.classWasHeldDialogMessage': 'Confirm that this lesson took place? The lesson will remain and both parties will still need to confirm the actual lesson hours',
  'appointment.withdrawNotHeldDialogMessage': 'Withdraw the no-lesson confirmation? The lesson status will return to accepted',
  'appointment.lifecycleFailed': 'Failed to update lesson status. Please try again later.',
  'appointment.deleteForMe': 'Delete for me',
  'appointment.recall': 'Recall',
  'appointment.schedule': 'Schedule',
  'appointment.joinMeeting': 'Join video meeting',
  'appointment.statusAria': 'Schedule status: {status}',
  'appointment.status.pending': 'Pending',
  'appointment.status.accepted': 'Accepted',
  'appointment.status.rejected': 'Rejected',
  'appointment.status.rescheduling': 'Rescheduling',
  'appointment.status.cancelled': 'Cancelled',
  'appointment.status.not_held_pending': 'Awaiting no-lesson confirmation',
  'appointment.status.not_held': 'Lesson not held',
  'appointment.status.expired': 'Expired',

  'lessonHours.dialogAria': 'Fill lesson hours',
  'lessonHours.close': 'Close',
  'lessonHours.hoursLabel': 'Lesson hours',
  'lessonHours.hoursUnit': 'hours',
  'lessonHours.hint': 'Lesson hours are calculated in 0.25-hour increments. Values that are not multiples of 0.25 will be rounded to the nearest valid value.',
  'lessonHours.submit': 'Submit',
  'lessonHours.submitting': 'Submitting...',
  'lessonHours.pendingCourse': 'Course',
  'lessonHours.student': 'Student',
  'lessonHours.mentor': 'Mentor',
  'lessonHours.pendingTitleMentor': 'The student disputed this lesson’s hours',
  'lessonHours.pendingTitleStudent': 'Confirm the actual hours for this lesson',
  'lessonHours.pendingSubtitle': 'This window will stay open until you respond',
  'lessonHours.pendingAutoConfirmSubtitle': 'Confirm or dispute by {deadline}. After the deadline, the mentor-submitted hours will be confirmed and deducted automatically.',
  'lessonHours.originalHours': 'Original mentor-submitted hours',
  'lessonHours.mentorSubmittedHours': 'Mentor-submitted hours',
  'lessonHours.studentClaimedHours': 'Student-claimed hours',
  'lessonHours.tipMentor': 'Confirm to close with the student’s dispute; submit for platform review to escalate.',
  'lessonHours.tipStudent': 'If the hours are incorrect, dispute directly so the mentor can review again.',
  'lessonHours.submitPlatformReview': 'Submit for Platform Review',
  'lessonHours.dispute': 'Dispute',
  'lessonHours.confirmStudentDispute': 'Confirm Student Dispute',
  'lessonHours.confirmHours': 'Confirm Hours',
  'lessonHours.goTopUp': 'Top Up',
  'lessonHours.pendingAria': 'Pending lesson-hour confirmation',
  'lessonHours.queue': '{count} lesson-hour confirmations remain and will be handled one by one',
  'lessonHours.processing': 'Processing...',

  'settings.title': 'Settings & Data',
  'settings.aria': 'Settings & Data',
  'settings.options': 'Settings options',
  'settings.backToOptions': 'Back to settings',
  'settings.sectionContent': '{section} content',
  'settings.section.profile': 'Personal Info',
  'settings.section.studentData': 'Student Data',
  'settings.section.mentorData': 'Mentor Data',
  'settings.section.security': 'Security & Privacy',
  'settings.section.notifications': 'Notifications',
  'settings.section.payments': 'Payments & Billing',
  'settings.section.language': 'Language & Preferences',
  'settings.viewMentorAgreement': 'View agreement',
  'settings.viewMentorAgreementAria': 'View signed mentor agreement',
  'settings.toast.mentorSubmitted': 'Mentor application submitted. We will review it soon.',
  'settings.resubmitMentorApplication': 'Resubmit',
  'settings.mentorStatus.resumePending': 'Resume under review',
  'settings.mentorStatus.interviewPending': 'Interview pending',
  'settings.mentorStatus.approved': 'Approved',
  'settings.mentorStatus.resumeRejected': 'Resume rejected',
  'settings.mentorStatus.interviewRejected': 'Interview not passed',

  'profile.email': 'Email',
  'profile.degree': 'Degree',
  'profile.school': 'School',
  'profile.timeZone': 'Time zone',
  'profile.availability': 'Availability',
  'profile.choose': 'Select',
  'profile.timeZoneFallback': 'Time zone',
  'profile.degreeSaved': 'Degree saved',
  'profile.schoolSaved': 'School saved',
  'profile.timeZoneSaved': 'Time zone saved',
  'profile.availabilitySaved': 'Availability saved',
  'profile.availabilityDays': 'Set for {count} days',
  'profile.availabilityEditor.loginHint': 'Log in to set availability',
  'profile.availabilityEditor.timeZone': 'Time zone: {zone}',
  'profile.availabilityEditor.calendar': 'Availability calendar',
  'profile.availabilityEditor.selector': 'Availability selector',
  'profile.degree.bachelor': "Bachelor's",
  'profile.degree.master': "Master's",
  'profile.degree.phd': 'PhD',

  'studentData.aria': 'Student data',
  'studentData.overview': 'Student data overview',
  'studentData.metrics': 'Student data metrics',
  'studentData.changeAvatar': 'Change avatar',
  'studentData.subtitleFallback': 'Mentory Student',
  'studentData.classes': 'Classes',
  'studentData.reviews': 'Reviews',
  'studentData.joined': 'Joined Mentory',
  'studentData.unit.times': 'times',
  'studentData.unit.items': 'reviews',
  'studentData.unit.days': 'days',
  'studentData.writtenReviews': 'Reviews I wrote',
  'studentData.writtenReviewsList': 'Reviews I wrote list',
  'studentData.emptyReviews': 'No reviews yet',
  'studentData.mentorFallback': 'Mentor',

  'mentorData.aria': 'Mentor data',
  'mentorData.overview': 'Mentor data overview',
  'mentorData.metrics': 'Mentor data metrics',
  'mentorData.changeAvatar': 'Change avatar',
  'mentorData.subtitleFallback': 'Mentory Mentor',
  'mentorData.classes': 'Classes',
  'mentorData.receivedReviews': 'Reviewed',
  'mentorData.joined': 'Joined Mentory',
  'mentorData.aboutMeReviews': 'Reviews about me',
  'mentorData.aboutMeReviewsList': 'Reviews about me list',

  'reviews.emptyText': 'No written review',
  'reviews.ratingAria': 'Rating {rating}',

  'security.password': 'Login password',
  'security.passwordSet': 'Set',
  'security.newPasswordPlaceholder': 'New password (at least 6 characters)',
  'security.confirmPasswordPlaceholder': 'Confirm new password',
  'security.minPassword': 'Password must be at least 6 characters',
  'security.passwordMismatch': 'The two passwords do not match',
  'security.passwordSaved': 'Password changed',
  'security.passwordSaveFailed': 'Change failed. Please try again later.',
  'security.dataPersonalization': 'Data personalization',
  'security.dataPersonalizationValue': 'Used to improve recommendations',

  'notifications.email': 'Email notifications',
  'notifications.emailValue': 'Important updates and course reminders, excluding verification codes',
  'notifications.enabled': 'Email reminders enabled',
  'notifications.disabled': 'Email reminders disabled',
  'notifications.newRequests': 'New course request alerts',
  'notifications.newRequestsValue': 'Notify mentors by email when a new request matches their preferences',
  'notifications.directions': 'Course directions',
  'notifications.types': 'Course types',
  'notifications.notSelected': 'Not selected',
  'notifications.selectionSummary': '{labels} and more ({count} selected)',
  'notifications.selectedCount': '{count} selected',
  'notifications.searchDirections': 'Search course directions',
  'notifications.noResults': 'No matching directions',
  'notifications.clear': 'Clear',
  'notifications.selectAll': 'Select all',
  'notifications.selectResults': 'Select results',
  'notifications.close': 'Close',
  'notifications.cancel': 'Cancel',
  'notifications.save': 'Save',
  'notifications.saving': 'Saving…',
  'notifications.saved': 'Alert preferences saved',
  'notifications.chooseCriteria': 'Select course directions and course types first',
  'notifications.saveFailed': 'Could not save. Please try again.',
  'notifications.loading': 'Loading alert preferences…',
  'notifications.loadFailed': 'Could not load alert preferences',
  'notifications.retry': 'Retry',

  'payments.payment': 'Payments',
  'payments.income': 'Income',
  'payments.loading': 'Loading...',
  'payments.noRecords': 'No records',
  'payments.rechargeRecords': 'Top-up records ({count})',
  'payments.incomeRecords': 'Income records ({count})',
  'payments.loadPaymentsFailed': 'Failed to load payment records. Please try again later.',
  'payments.loadIncomeFailed': 'Failed to load income records. Please try again later.',
  'payments.noRechargeRecords': 'No top-up records',
  'payments.noIncomeRecords': 'No income records',
  'payments.timeZone': 'Time zone',
  'payments.time': 'Time',
  'payments.amount': 'Amount',
  'payments.courseHours': 'Course hours',
  'payments.teachingHours': 'Teaching hours',
  'payments.otherDirection': 'Other course direction',
  'payments.otherType': 'Other type',

  'language.language': 'Language',
  'language.current.zh-CN': '简体中文',
  'language.current.en': 'English',
  'language.dialogTitle': 'Change Language',
  'language.dialogDescription': 'Choose the language used across Mentory on this device',
  'language.option.zh-CN': '简体中文',
  'language.option.en': 'English',
  'language.saved': 'Language changed',
  'language.homeOrder': 'Home course order',
  'language.homeOrder.custom': 'Custom',
  'language.homeOrder.default': 'Default order',
  'language.homeOrderHint': 'Drag course cards to adjust the home course order. Changes save automatically.',
  'language.restoreDefault': 'Restore default',
  'language.orderAria': 'Home course order',
  'language.dragAria': 'Drag to reorder: {label}',
  'language.orderSaved': 'Home course order saved',
  'language.orderDefaultRestored': 'Default order restored',
};

const COURSE_DIRECTION_EN_LABELS = {
  'cs-foundation': 'Programming Fundamentals',
  algo: 'Data Structures & Algorithms',
  ml: 'Machine Learning',
  'ai-large-model': 'AI Large Models',
  'data-analysis': 'Data Analysis',
  'business-analytics': 'Business Analytics',
  'advanced-math': 'Advanced Mathematics',
  statistics: 'Probability & Statistics',
  physics: 'Physics',
  'electrical-electronics': 'Electrical & Electronics',
  'mechanical-engineering': 'Mechanical Engineering',
  'civil-structural': 'Civil / Structural',
  'life-science': 'Life Sciences',
  'public-health': 'Public Health',
  chemistry: 'Chemistry',
  'materials-science': 'Materials Science',
  'software-engineering': 'Software Engineering',
  'cloud-computing': 'Cloud Computing',
  cybersecurity: 'Cybersecurity',
  finance: 'Finance',
  accounting: 'Accounting',
  economics: 'Economics',
  marketing: 'Marketing',
  management: 'Management',
  psychology: 'Psychology',
  education: 'Education',
  'design-creative': 'Design / Creative',
  linguistics: 'Linguistics',
  'communication-studies': 'Communication Studies',
  law: 'Law',
  writing: 'Academic Writing & Editing',
  'career-coaching': 'Career Coaching',
  others: 'Other Course Direction',
};

const COURSE_TYPE_EN_LABELS = {
  'course-selection': 'Course Selection',
  'pre-study': 'Pre-study',
  'assignment-project': 'Projects',
  'final-review': 'Final Review',
  'in-class-support': 'Graduation Thesis',
  others: 'Other Type',
};

const COURSE_DIRECTION_ZH_TO_ID = {
  '编程基础': 'cs-foundation',
  '数据结构与算法': 'algo',
  '机器学习': 'ml',
  'AI 大模型': 'ai-large-model',
  '数据分析': 'data-analysis',
  '商业分析': 'business-analytics',
  '高等数学': 'advanced-math',
  '概率与统计': 'statistics',
  '物理学': 'physics',
  '电气与电子': 'electrical-electronics',
  '机械工程': 'mechanical-engineering',
  '土木 / 结构': 'civil-structural',
  '生命科学': 'life-science',
  '健康与公共卫生': 'public-health',
  '化学': 'chemistry',
  '材料科学': 'materials-science',
  '软件工程': 'software-engineering',
  '云计算': 'cloud-computing',
  '网络安全': 'cybersecurity',
  '金融学': 'finance',
  '会计学': 'accounting',
  '经济学': 'economics',
  '市场营销': 'marketing',
  '管理学': 'management',
  '心理学': 'psychology',
  '教育学': 'education',
  '设计 / 创意': 'design-creative',
  '语言学': 'linguistics',
  '传播学': 'communication-studies',
  '法律': 'law',
  '论文写作与润色': 'writing',
  '求职辅导': 'career-coaching',
  '其它课程方向': 'others',
  '其他课程方向': 'others',
};

const COURSE_TYPE_ZH_TO_ID = {
  '选课指导': 'course-selection',
  '课前预习': 'pre-study',
  '作业项目': 'assignment-project',
  '期末复习': 'final-review',
  '毕业论文': 'in-class-support',
  '其它类型': 'others',
  '其他课程类型': 'others',
  '其它课程类型': 'others',
};

const normalizeLanguage = (value) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === 'zh' || raw === 'zh-CN') return 'zh-CN';
  if (raw === 'en' || raw === 'en-US' || raw === 'en-GB') return 'en';
  return SUPPORTED_LANGUAGE_VALUES.has(raw) ? raw : 'zh-CN';
};

export const getStoredLanguage = () => {
  if (typeof window === 'undefined') return 'zh-CN';
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return 'zh-CN';
  }
};

const formatMessage = (template, replacements) => {
  if (!replacements || typeof replacements !== 'object') return template;
  return String(template).replace(/\{(\w+)\}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(replacements, key)
      ? String(replacements[key])
      : match
  ));
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getStoredLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
        window.dispatchEvent(new CustomEvent('mentory:language-changed', { detail: { language: normalized } }));
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'en' ? 'en' : 'zh-CN';
    }
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleStorage = (event) => {
      if (event.key === LANGUAGE_STORAGE_KEY) setLanguageState(normalizeLanguage(event.newValue));
    };
    const handleLanguageChanged = (event) => {
      setLanguageState(normalizeLanguage(event?.detail?.language));
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('mentory:language-changed', handleLanguageChanged);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('mentory:language-changed', handleLanguageChanged);
    };
  }, []);

  const t = useCallback((key, fallback, replacements) => {
    const fallbackText = typeof fallback === 'string' ? fallback : key;
    if (language !== 'en') return formatMessage(fallbackText, replacements);
    return formatMessage(en[key] || fallbackText, replacements);
  }, [language]);

  const getCourseDirectionLabel = useCallback((id, fallback) => {
    if (language !== 'en') return fallback;
    return COURSE_DIRECTION_EN_LABELS[id] || fallback;
  }, [language]);

  const getCourseDirectionDisplayLabel = useCallback((idOrLabel, fallback) => {
    const raw = typeof idOrLabel === 'string' ? idOrLabel.trim() : idOrLabel;
    const fallbackRaw = typeof fallback === 'string' ? fallback.trim() : fallback;
    const id = COURSE_DIRECTION_EN_LABELS[raw]
      ? raw
      : COURSE_DIRECTION_ZH_TO_ID[raw]
        || (COURSE_DIRECTION_EN_LABELS[fallbackRaw] ? fallbackRaw : COURSE_DIRECTION_ZH_TO_ID[fallbackRaw]);
    if (language !== 'en') return fallbackRaw || raw;
    return COURSE_DIRECTION_EN_LABELS[id] || fallbackRaw || raw;
  }, [language]);

  const getCourseTypeLabel = useCallback((idOrLabel, fallback) => {
    const raw = typeof idOrLabel === 'string' ? idOrLabel.trim() : idOrLabel;
    const fallbackRaw = typeof fallback === 'string' ? fallback.trim() : fallback;
    const id = COURSE_TYPE_EN_LABELS[raw]
      ? raw
      : COURSE_TYPE_ZH_TO_ID[raw]
        || (COURSE_TYPE_EN_LABELS[fallbackRaw] ? fallbackRaw : COURSE_TYPE_ZH_TO_ID[fallbackRaw]);
    if (language !== 'en') return fallbackRaw || raw;
    return COURSE_TYPE_EN_LABELS[id] || fallbackRaw || raw;
  }, [language]);

  const value = useMemo(() => ({
    language,
    isEnglish: language === 'en',
    setLanguage,
    t,
    getCourseDirectionLabel,
    getCourseDirectionDisplayLabel,
    getCourseTypeLabel,
  }), [getCourseDirectionDisplayLabel, getCourseDirectionLabel, getCourseTypeLabel, language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useI18n() {
  const value = useContext(LanguageContext);
  if (value) return value;
  return {
    language: 'zh-CN',
    isEnglish: false,
    setLanguage: () => {},
    t: (_key, fallback, replacements) => formatMessage(typeof fallback === 'string' ? fallback : _key, replacements),
    getCourseDirectionLabel: (_id, fallback) => fallback,
    getCourseDirectionDisplayLabel: (_id, fallback) => fallback,
    getCourseTypeLabel: (_id, fallback) => fallback,
  };
}
